import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";

const PREFIX = "ServerSelectionControls";

const rootClasses = {
  multiSelectAlert: `${PREFIX}-multiSelectAlert`,
  multiSelectButton: `${PREFIX}-multiSelectButton`,
  multiSelectCloseButton: `${PREFIX}-multiSelectCloseButton`,
  multiSelectSpacer: `${PREFIX}-multiSelectSpacer`,
  multiSelectionNumber: `${PREFIX}-multiSelectionNumber`,
  multiSelectionText: `${PREFIX}-multiSelectionText`,
  multiselectContainerV2: `${PREFIX}-multiselectContainerV2`,
  toolbarActionButton: `${PREFIX}-toolbarActionButton`,
};

const Root = styled("div")(({ theme }) => ({
  height: 50,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  [`& .${rootClasses.multiselectContainerV2}`]: {
    alignItems: "center",
    backgroundColor: theme.palette.background.multiselectControlBar,
    borderRadius: 20,
    display: "flex",
    padding: 2,
    width: "100%",
    [`& .${rootClasses.toolbarActionButton}`]: {
      // Toolbar action buttons have reversed colors
      color: theme.palette.background.multiselectControlBar,
      backgroundColor: theme.palette.text.multiselectControlBar,
      // Border is the same color as the backgroundColor, but it makes
      // the two kinds of buttons line up better.
      borderWidth: 1,
      borderColor: theme.palette.text.multiselectControlBar,
      borderStyle: "solid",
      borderRadius: 15,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 15,
      paddingRight: 15,
      marginRight: 8,
      marginLeft: 0,
      ["&:disabled"]: {
        color: alpha(theme.palette.background.multiselectControlBar, 0.5),
      },
      [`& .MuiButton-endIcon`]: {
        marginLeft: 4,
      },
    },
    [`& .${rootClasses.multiSelectCloseButton}`]: {
      color: theme.palette.text.multiselectControlBar,
      marginLeft: 8,
      marginRight: 8,
      marginTop: 4,
      marginBottom: 4,
      padding: 0,
      ["&:hover"]: {
        backgroundColor: alpha(theme.palette.text.multiselectControlBar, 0.08),
      },
    },
    [`& .${rootClasses.multiSelectButton}`]: {
      // Select All button
      color: theme.palette.text.multiselectControlBar,
      borderWidth: 1,
      borderColor: theme.palette.text.multiselectControlBar,
      borderStyle: "solid",
      borderRadius: 15,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 15,
      paddingRight: 15,
      marginLeft: 16,
      marginRight: -8, // Added to width of multiSelectSpacer
      ["&:hover"]: {
        backgroundColor: alpha(theme.palette.text.multiselectControlBar, 0.08),
      },
    },
    [`& .${rootClasses.multiSelectionNumber}`]: {
      fontWeight: "bold",
    },
    [`& .${rootClasses.multiSelectSpacer}`]: {
      // Space to the left of the action buttons
      width: 16,
    },
    [`& .${rootClasses.multiSelectionText}`]: {
      color: theme.palette.text.multiselectControlBar,
    },
  },
  [`& .${rootClasses.toolbarActionButton}`]: {
    margin: "0 10px",
    fontWeight: "bold",
    padding: "9px 18px",
    textTransform: "none",
    color: theme.palette.text.primary,
    "&.Mui-disabled": {
      color: theme.palette.action.disabled,
    },
  },
}));

const ServerSelectionControls = ({
  checkboxSelectionActions,
  fetchAllIds,
  onSelectionControlChange,
  pageSize,
  selectedIds,
  totalItems,
}) => {
  const [multiSelectAnchorEl, setMultiSelectAnchorEl] = useState(null);

  const SelectionInfo = () => {
    if (selectedIds.length === totalItems) {
      return (
        <span className={rootClasses.multiSelectionText}>
          {"All "}
          <span className={rootClasses.multiSelectionNumber}>
            {selectedIds.length}
          </span>
          {" items are selected"}
        </span>
      );
    } else if (selectedIds.length > 1 && selectedIds.length === pageSize) {
      return (
        <span className={rootClasses.multiSelectionText}>
          {"All "}
          <span className={rootClasses.multiSelectionNumber}>
            {selectedIds.length}
          </span>
          {" items on this page are selected"}
        </span>
      );
    } else if (selectedIds.length === 1) {
      return (
        <span className={rootClasses.multiSelectionText}>
          <span className={rootClasses.multiSelectionNumber}>
            {selectedIds.length}
          </span>
          {` item out of ${totalItems} are selected`}
        </span>
      );
    } else if (selectedIds.length > 1) {
      return (
        <span className={rootClasses.multiSelectionText}>
          <span className={rootClasses.multiSelectionNumber}>
            {selectedIds.length}
          </span>
          {` items out of ${totalItems} are selected`}
        </span>
      );
    }
  };

  const handleDeselect = () => {
    onSelectionControlChange([]);
  };

  const handleSelectAll = async () => {
    onSelectionControlChange(await fetchAllIds());
  };

  return (
    <Root>
      <div className={rootClasses.multiselectContainerV2}>
        <IconButton
          title="Unselect all items"
          className={rootClasses.multiSelectCloseButton}
          onClick={handleDeselect}
        >
          <CloseIcon />
        </IconButton>
        <span className={rootClasses.multiSelectionText}>
          <SelectionInfo />
        </span>
        {selectedIds.length !== totalItems && (
          <Button
            className={rootClasses.multiSelectButton}
            onClick={handleSelectAll}
          >
            {`Select all ${totalItems}`}
          </Button>
        )}
        <div className={rootClasses.multiSelectSpacer} />
        {checkboxSelectionActions.map((selectAction, idx) => {
          if (selectAction.type === "dropdown") {
            return (
              <Box key={idx}>
                <Button
                  onClick={(event) =>
                    setMultiSelectAnchorEl(event.target.closest("button"))
                  }
                  className={rootClasses.toolbarActionButton}
                  endIcon={<ArrowDropDownIcon />}
                >
                  {selectAction.label}
                </Button>
                <Menu
                  keepMounted
                  id="simple-menu"
                  anchorEl={multiSelectAnchorEl}
                  open={Boolean(multiSelectAnchorEl)}
                  disableScrollLock={true}
                  onClose={() => setMultiSelectAnchorEl(null)}
                >
                  {selectAction.options.map((option, idx) => (
                    <MenuItem
                      key={idx}
                      onClick={() => {
                        selectAction.onClick(option);
                        setMultiSelectAnchorEl(null);
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            );
          } else {
            return (
              <Tooltip title={selectAction.tooltip}>
                <span>
                  <Button
                    className={rootClasses.toolbarActionButton}
                    onClick={selectAction.onClick}
                    disabled={selectAction.disabled}
                  >
                    {selectAction.label}
                  </Button>
                </span>
              </Tooltip>
            );
          }
        })}
      </div>
    </Root>
  );
};

ServerSelectionControls.propTypes = {
  checkboxSelectionActions: PropTypes.arrayOf(PropTypes.object),
  fetchAllIds: PropTypes.func.isRequired,
  pageSize: PropTypes.number.isRequired,
  selectedIds: PropTypes.array.isRequired,
  totalItems: PropTypes.number.isRequired,
};

export default ServerSelectionControls;
